import { z } from 'zod';
import { Translations } from 'types/Common';
import { ShopItemsFilters } from 'store/shop';
import { FrequencyUnit } from 'types/general';
import { UploadedFile } from 'store/files';
import { benefitsFiltersSchema } from './benefits.schemas';

export const benefitCategories = [
  'financial',
  'insurances',
  'wellness',
  'perks',
  'pensions',
  'timeOff',
  'education',
  'generalEmployeeWelfare',
  'companyDiscounts',
  'partnerDiscounts',
] as const;
export type BenefitCategory = (typeof benefitCategories)[number];

export const benefitPublishStatuses = ['published', 'unpublished'] as const;
export type BenefitPublishStatus = (typeof benefitPublishStatuses)[number];

export const benefitStatuses = [
  'draft',
  'inactive',
  'active',
  'expiresSoon',
  'expired',
] as const;
export type BenefitStatus = (typeof benefitStatuses)[number];

export interface BaseBenefit {
  id: string;
  name: string;
}

export interface ListBenefit extends BaseBenefit {
  type: BenefitCategory;
  deactivatedAt: string | null;
  activatedAt: string | null;
  employeeCount: number;
  investmentAmount: number;
  value: number;
  publishStatus: BenefitPublishStatus;
  status: BenefitStatus;
  allowClaimReimbursement: boolean;
  trackBalance: boolean;
  isArchived: boolean;
}

export interface Benefit extends Omit<ListBenefit, 'trackBalance'> {
  investmentAmount: number;
  employeeContribution: number;
  investmentPeriod: FrequencyUnit;
  isVisibleForEmployees: boolean;
  isAccumulating: boolean;
  isBalanceTrackingEnabled: boolean;
  internalComment: string;
  isPreview: boolean;
  previewDate: string | null;
  publishedAt: string | null;
  employeeCount: number;
  groupCount: number;
  isArchived: boolean;
  allocationPriority: number;
  taxExemptionCountry: string | null;
}

export interface BenefitContent {
  translations: Translations<{
    title: string;
    description: string;
    image: UploadedFile | null;
    links: Array<{ name: string; url: string; index: number }>;
    faqs: Array<{ question: string; answer: string; index: number }>;
    attachments: Array<{ name: string; file: UploadedFile; index: number }>;
  }>;
}

export interface BenefitCategoryContent {
  id: string;
  category: BenefitCategory;
  label: string;
  translations: Translations<{ label: string }>;
}

export type BenefitShopItemsConfig = Pick<
  ShopItemsFilters,
  | 'price'
  | 'items'
  | 'countries'
  | 'cities'
  | 'suppliers'
  | 'benefitCategories'
  | 'categories'
  | 'taxExemptionStatus'
  | 'taxExemptionCountries'
> | null;

export interface EmployeeBenefitsFilters {
  trackBalance: boolean;
}

export type BenefitsFilters = z.infer<typeof benefitsFiltersSchema>;

export type LiteBenefitsFilters = Pick<BenefitsFilters, 'name'>;
